import i18n from '@/libs/i18n'

export enum Errors {
    InternalServerError = 1,
    InvalidData         = 2,
    NotFound            = 3,
    Unauthorized        = 4,
    FailedToInsert      = 5,
    ItemAlreadyExists   = 6,
    Forbidden           = 7,
    IncorrectStatus     = 8,
    /**
     * A route of a module this hotel has not bought.
     *
     * It shares 403 with Forbidden and is not the same thing: Forbidden is "this
     * account may not", which another right can fix, and this is "this house has not
     * got the module", which nobody signed in can fix at all. The code is the only
     * thing that tells them apart (docs/MODULI.md §3.2).
     */
    ModuleNotLicensed   = 19,
}

/**
 * Turns the API's {"error": {"code": n, "message": …}} body into a message for the
 * desk. Reads through i18n.global so it works outside a setup context too.
 *
 * The server's own words win by default — see pms-web's libs/error.ts for the full
 * account. ItemAlreadyExists, IncorrectStatus and the catch-all default all read
 * body.message first now; a caller's fallback is used only when the server sent
 * nothing to show.
 */
export const parseError = (errorObject: unknown, fallback = ''): string => {
    const t = i18n.global.t
    const raw = (errorObject as { error?: unknown })?.error

    // A bare-string body (PrintHTTP.WriteErrorMessage: {"error": "..."}) carries no
    // code for anything below to key on — see pms-web's libs/error.ts for the fuller
    // account (found live 2026-09-03: a decode failure answered a 400 this way, and
    // the toast said only "could not save").
    if (typeof raw === 'string') return raw || fallback || t('errors.generic_error')

    const body = raw as { code?: number, message?: string } | undefined
    const code = body?.code

    if (!code) return fallback || t('errors.generic_error')

    switch (code) {
        case Errors.InvalidData:       return t('errors.invalid_data')
        case Errors.NotFound:          return t('errors.not_found')
        case Errors.Unauthorized:      return t('errors.unauthorized')
        case Errors.Forbidden:         return t('errors.unauthorized')
        case Errors.ItemAlreadyExists: return body?.message || fallback || t('errors.conflict')
        case Errors.IncorrectStatus:   return body?.message || fallback || t('errors.generic_error')
        // Said plainly rather than as "you have no right": nobody here can be given
        // this one. The full-screen notice (components/ModuleNotice.vue) is what the
        // person actually reads — this is only for a call that answered before it.
        case Errors.ModuleNotLicensed: return t('license.title')
        default:                       return body?.message || fallback || t('errors.generic_error')
    }
}
